import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  LayoutDashboard, Users, Shield, Diamond, Settings, LogOut,
  Search, Ban, Crown, Trash2, AlertTriangle,
  Plus, X, Key, Menu, RefreshCw,
  TrendingUp, FileText, Image, DollarSign, Wallet, GraduationCap, CheckCircle,
  UserPlus, FileSpreadsheet, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { resolveImageUrl } from '@/lib/api';
import ReferralsAdminPage from './ReferralsPage';
import DataExportAdminPage from './DataExportPage';
import OpportunitiesAdminPage from './OpportunitiesAdminPage';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════ */
/*                AUTH HOOK                    */
/* ═══════════════════════════════════════════ */
const useAdminAuth = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const adminUser = JSON.parse(localStorage.getItem('admin_user') || 'null');
  const logout = () => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); navigate('/admin-login'); };
  const getHeaders = () => ({ Authorization: `Bearer ${token}` });
  return { token, adminUser, logout, getHeaders, isAuthenticated: !!token };
};

function AdminProtectedRoute({ children }) {
  const { isAuthenticated } = useAdminAuth();
  if (!isAuthenticated) return <Navigate to="/admin-login" replace />;
  return children;
}

function Spin() {
  return <div className="flex items-center justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" /></div>;
}

function Stat({ title, value, icon: Icon, color = 'emerald' }) {
  const c = { emerald: 'bg-emerald-500/15 text-emerald-400', blue: 'bg-blue-500/15 text-blue-400', amber: 'bg-amber-500/15 text-amber-400', red: 'bg-red-500/15 text-red-400', purple: 'bg-purple-500/15 text-purple-400' };
  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid={`stat-${title}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${c[color]}`}><Icon className="h-5 w-5" /></div>
        <div><p className="text-2xl font-bold text-white">{value}</p><p className="text-xs text-zinc-400">{title}</p></div>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════════════════ */
/*              1. DASHBOARD                  */
/* ═══════════════════════════════════════════ */
function DashboardPage() {
  const { getHeaders } = useAdminAuth();
  const [s, setS] = useState(null);
  const [f, setF] = useState(null);
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [sr, fr, ar] = await Promise.all([
          axios.get(`${API}/api/admin/dashboard/stats`, { headers: getHeaders() }),
          axios.get(`${API}/api/admin/finance`, { headers: getHeaders() }),
          axios.get(`${API}/api/admin/awa`, { headers: getHeaders() }),
        ]);
        setS(sr.data); setF(fr.data); setA(ar.data);
      } catch { toast.error('Erro ao carregar dashboard'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Spin />;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="text-2xl font-bold text-white">Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat title="Usuários" value={s?.users?.total || 0} icon={Users} color="blue" />
        <Stat title="Premium" value={s?.users?.premium || 0} icon={Crown} color="purple" />
        <Stat title="AWA Total" value={(a?.total_awa_circulation || 0).toLocaleString()} icon={Wallet} color="emerald" />
        <Stat title="Receita (R$)" value={`R$ ${(f?.supervision?.total_revenue_brl || 0).toFixed(2)}`} icon={DollarSign} color="amber" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800"><CardHeader className="pb-2"><CardTitle className="text-sm text-zinc-400">Posts</CardTitle></CardHeader><CardContent><span className="text-3xl font-bold text-white">{s?.posts?.total || 0}</span><p className="text-xs text-zinc-400 mt-1">Hoje: {s?.posts?.today || 0}</p></CardContent></Card>
        <Card className="bg-zinc-900 border-zinc-800"><CardHeader className="pb-2"><CardTitle className="text-sm text-zinc-400">Fundadores</CardTitle></CardHeader><CardContent><span className="text-3xl font-bold text-white">{s?.users?.founder || 0}</span></CardContent></Card>
        <Card className="bg-zinc-900 border-zinc-800"><CardHeader className="pb-2"><CardTitle className="text-sm text-zinc-400">Whitelist</CardTitle></CardHeader><CardContent><span className="text-3xl font-bold text-white">{s?.whitelist?.active || 0}</span><p className="text-xs text-zinc-400 mt-1">colaboradores ativos</p></CardContent></Card>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/*              2. USUÁRIOS                   */
/* ═══════════════════════════════════════════ */
function UsersPage() {
  const { getHeaders } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search) p.append('search', search);
      if (planFilter !== 'all') p.append('plan', planFilter);
      p.append('limit', '20'); p.append('offset', String(page * 20));
      const r = await axios.get(`${API}/api/admin/users?${p}`, { headers: getHeaders() });
      setUsers(r.data.users); setTotal(r.data.total);
    } catch { toast.error('Erro ao carregar'); } finally { setLoading(false); }
  }, [search, planFilter, page]);

  useEffect(() => { load(); }, [load]);

  const action = async (userId, act, extra = {}) => {
    try {
      await axios.post(`${API}/api/admin/users/action`, { user_id: userId, action: act, ...extra }, { headers: getHeaders() });
      toast.success('Ação realizada'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Tem certeza que deseja DELETAR este usuário? Ação irreversível.')) return;
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`, { headers: getHeaders() });
      toast.success('Usuário deletado'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      <h1 className="text-2xl font-bold text-white">Usuários</h1>
      <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-4 flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-zinc-800 border-zinc-700 text-white" />
        </div>
        <Select value={planFilter} onValueChange={setPlanFilter}><SelectTrigger className="w-[130px] bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="free">Free</SelectItem><SelectItem value="premium">Premium</SelectItem><SelectItem value="founder">Fundador</SelectItem></SelectContent></Select>
      </CardContent></Card>

      <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-0">
        {loading ? <Spin /> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-800/60"><tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Usuário</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Plano</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Data</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Ações</th>
              </tr></thead>
              <tbody className="divide-y divide-zinc-800">
                {users.map(u => (
                  <tr key={u.user_id} className="hover:bg-zinc-800/40">
                    <td className="px-4 py-3"><div className="font-medium text-white">{u.name || '-'}</div><div className="text-xs text-zinc-400">{u.email}</div></td>
                    <td className="px-4 py-3"><Badge variant="outline" className="border-zinc-600 text-zinc-300 text-xs">{(u.roles || ['user']).join(', ')}</Badge></td>
                    <td className="px-4 py-3">{u.is_founder ? <Badge className="bg-amber-500/20 text-amber-400">Fundador</Badge> : u.is_premium ? <Badge className="bg-purple-500/20 text-purple-400">Premium</Badge> : <Badge variant="secondary" className="bg-zinc-700 text-zinc-300">Free</Badge>}</td>
                    <td className="px-4 py-3">{u.is_banned ? <Badge variant="destructive">Banido</Badge> : <Badge className="bg-emerald-500/20 text-emerald-400">Ativo</Badge>}</td>
                    <td className="px-4 py-3 text-xs text-zinc-400">{u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {u.is_banned
                          ? <Button size="sm" variant="outline" className="text-emerald-400 border-emerald-400/30 h-7 text-xs" onClick={() => action(u.user_id, 'unban')}>Desbanir</Button>
                          : <Button size="sm" variant="outline" className="text-red-400 border-red-400/30 h-7 px-2" onClick={() => action(u.user_id, 'ban')}><Ban className="h-3 w-3" /></Button>
                        }
                        <Select onValueChange={plan => action(u.user_id, 'change_plan', { plan })}><SelectTrigger className="w-[85px] h-7 bg-zinc-800 border-zinc-700 text-xs text-white"><SelectValue placeholder="Plano" /></SelectTrigger><SelectContent><SelectItem value="free">Free</SelectItem><SelectItem value="premium">Premium</SelectItem><SelectItem value="founder">Fundador</SelectItem></SelectContent></Select>
                        <Button size="sm" variant="outline" className="text-red-400 border-red-400/30 h-7 px-2" onClick={() => deleteUser(u.user_id)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent></Card>
      <div className="flex justify-between items-center text-zinc-400 text-sm">
        <span>{users.length} de {total}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="border-zinc-700 text-zinc-300">Anterior</Button>
          <Button variant="outline" size="sm" disabled={(page + 1) * 20 >= total} onClick={() => setPage(p => p + 1)} className="border-zinc-700 text-zinc-300">Próximo</Button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/*              3. WHITELIST                  */
/* ═══════════════════════════════════════════ */
function WhitelistPage() {
  const { getHeaders } = useAdminAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', role: 'tester', expires_days: '', notes: '' });

  const load = async () => { setLoading(true); try { const r = await axios.get(`${API}/api/admin/whitelist`, { headers: getHeaders() }); setEntries(r.data.entries); } catch { toast.error('Erro whitelist'); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      const payload = { email: form.email, role: form.role, notes: form.notes || null, expires_at: form.expires_days ? new Date(Date.now() + parseInt(form.expires_days) * 86400000).toISOString() : null };
      await axios.post(`${API}/api/admin/whitelist`, payload, { headers: getHeaders() });
      toast.success('Adicionado'); setShowAdd(false); setForm({ email: '', role: 'tester', expires_days: '', notes: '' }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const remove = async (email) => { try { await axios.delete(`${API}/api/admin/whitelist/${encodeURIComponent(email)}`, { headers: getHeaders() }); toast.success('Removido'); load(); } catch { toast.error('Erro'); } };

  const roleBadge = (r) => {
    const m = { programmer: ['Programador', 'bg-blue-500/20 text-blue-400'], moderator: ['Moderador', 'bg-purple-500/20 text-purple-400'], tester: ['Testador', 'bg-emerald-500/20 text-emerald-400'], collaborator: ['Colaborador', 'bg-amber-500/20 text-amber-400'] };
    const [l, c] = m[r] || [r, 'bg-zinc-600 text-zinc-300'];
    return <Badge className={c}>{l}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="admin-whitelist">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Whitelist</h1>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild><Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="whitelist-add-btn"><Plus className="h-4 w-4 mr-2" />Adicionar</Button></DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader><DialogTitle className="text-white">Adicionar à Whitelist</DialogTitle><DialogDescription className="text-zinc-400">Adicione um e-mail com acesso à plataforma.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-zinc-300">E-mail</Label><Input placeholder="email@exemplo.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" data-testid="whitelist-email-input" /></div>
              <div><Label className="text-zinc-300">Função</Label><Select value={form.role} onValueChange={v => setForm({...form, role: v})}><SelectTrigger className="bg-zinc-800 border-zinc-700 text-white"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="programmer">Programador</SelectItem><SelectItem value="moderator">Moderador</SelectItem><SelectItem value="tester">Testador</SelectItem><SelectItem value="collaborator">Colaborador</SelectItem></SelectContent></Select></div>
              <div><Label className="text-zinc-300">Expira em (dias)</Label><Input type="number" placeholder="Permanente" value={form.expires_days} onChange={e => setForm({...form, expires_days: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" /></div>
              <div><Label className="text-zinc-300">Notas</Label><Input placeholder="Observações..." value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowAdd(false)} className="border-zinc-700">Cancelar</Button><Button onClick={add} className="bg-emerald-600 hover:bg-emerald-700" data-testid="whitelist-confirm-btn">Adicionar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <Spin /> : entries.length === 0 ? <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-8 text-center text-zinc-400">Nenhum e-mail na whitelist</CardContent></Card> : (
        <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-0"><div className="overflow-x-auto"><table className="w-full"><thead className="bg-zinc-800/60"><tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">E-mail</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Função</th>
          <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Expira</th>
          <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Ação</th>
        </tr></thead><tbody className="divide-y divide-zinc-800">
          {entries.map(e => (<tr key={e.email} className="hover:bg-zinc-800/40">
            <td className="px-4 py-3 text-white">{e.email}</td>
            <td className="px-4 py-3">{roleBadge(e.role)}</td>
            <td className="px-4 py-3 text-zinc-400 text-sm">{e.expires_at ? new Date(e.expires_at).toLocaleDateString('pt-BR') : 'Permanente'}</td>
            <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" className="text-red-400 border-red-400/30 h-7 px-2" onClick={() => remove(e.email)}><X className="h-3 w-3" /></Button></td>
          </tr>))}
        </tbody></table></div></CardContent></Card>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/*              4. FINANCEIRO                 */
/* ═══════════════════════════════════════════ */
function FinancePage() {
  const { getHeaders } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { try { const r = await axios.get(`${API}/api/admin/finance`, { headers: getHeaders() }); setData(r.data); } catch { toast.error('Erro'); } finally { setLoading(false); } })(); }, []);
  if (loading) return <Spin />;
  const sup = data?.supervision || {};
  return (
    <div className="space-y-6" data-testid="admin-finance">
      <h1 className="text-2xl font-bold text-white">Financeiro</h1>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat title="Receita Total" value={`R$ ${(sup.total_revenue_brl || 0).toFixed(2)}`} icon={DollarSign} color="emerald" />
        <Stat title="Plataforma" value={`R$ ${(sup.platform_share_brl || 0).toFixed(2)}`} icon={TrendingUp} color="blue" />
        <Stat title="Profissionais" value={`R$ ${(sup.professional_share_brl || 0).toFixed(2)}`} icon={Users} color="purple" />
      </div>
      <Card className="bg-zinc-900 border-zinc-800"><CardHeader><CardTitle className="text-white text-base">Supervisões</CardTitle></CardHeader><CardContent>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div><p className="text-2xl font-bold text-white">{sup.total_sessions || 0}</p><p className="text-xs text-zinc-400">Sessões Total</p></div>
          <div><p className="text-2xl font-bold text-white">{sup.admin_sessions || 0}</p><p className="text-xs text-zinc-400">Admin (100%)</p></div>
          <div><p className="text-2xl font-bold text-white">{sup.third_party_sessions || 0}</p><p className="text-xs text-zinc-400">Terceiros (80/20)</p></div>
        </div>
      </CardContent></Card>
      <Card className="bg-zinc-900 border-zinc-800"><CardHeader><CardTitle className="text-white text-base">Regras de Split</CardTitle></CardHeader><CardContent className="space-y-3">
        <div className="flex justify-between p-3 rounded bg-zinc-800"><span className="text-zinc-300">Supervisão Admin (Celso)</span><Badge className="bg-emerald-500/20 text-emerald-400">100% plataforma</Badge></div>
        <div className="flex justify-between p-3 rounded bg-zinc-800"><span className="text-zinc-300">Supervisão Terceiros</span><Badge className="bg-blue-500/20 text-blue-400">20% plataforma / 80% profissional</Badge></div>
      </CardContent></Card>
      <Card className="bg-zinc-900 border-zinc-800"><CardHeader><CardTitle className="text-white text-base">Planos</CardTitle></CardHeader><CardContent className="flex gap-6">
        <div><p className="text-lg font-bold text-white">{data?.plans?.premium_users || 0}</p><p className="text-xs text-zinc-400">Premium</p></div>
        <div><p className="text-lg font-bold text-white">{data?.plans?.founder_users || 0}</p><p className="text-xs text-zinc-400">Fundadores</p></div>
      </CardContent></Card>
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/*              5. AWA WALLET                 */
/* ═══════════════════════════════════════════ */
function AwaPage() {
  const { getHeaders } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adjustForm, setAdjustForm] = useState({ user_email: '', amount: '', reason: '' });
  const [adjusting, setAdjusting] = useState(false);

  const load = async () => { try { const r = await axios.get(`${API}/api/admin/awa`, { headers: getHeaders() }); setData(r.data); } catch { toast.error('Erro'); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const adjustAwa = async () => {
    if (!adjustForm.user_email || !adjustForm.amount || !adjustForm.reason) { toast.error('Preencha todos os campos'); return; }
    setAdjusting(true);
    try {
      const r = await axios.post(`${API}/api/admin/awa/adjust`, { user_email: adjustForm.user_email, amount: parseInt(adjustForm.amount), reason: adjustForm.reason }, { headers: getHeaders() });
      toast.success(`${r.data.message}. Novo saldo: ${r.data.new_balance} AWA`);
      setAdjustForm({ user_email: '', amount: '', reason: '' }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); } finally { setAdjusting(false); }
  };

  if (loading) return <Spin />;
  return (
    <div className="space-y-6" data-testid="admin-awa">
      <h1 className="text-2xl font-bold text-white">AWA Wallet</h1>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat title="AWA em Circulação" value={(data?.total_awa_circulation || 0).toLocaleString()} icon={Wallet} color="emerald" />
        <Stat title="Valor em R$" value={`R$ ${(data?.total_brl_value || 0).toFixed(2)}`} icon={DollarSign} color="blue" />
        <Stat title="Carteiras" value={data?.total_wallets || 0} icon={Users} color="amber" />
      </div>
      <Card className="bg-zinc-900 border-zinc-800 p-3"><span className="text-emerald-400 font-medium text-sm">Conversão: 1000 AWA = R$ 1,00</span></Card>
      <Card className="bg-zinc-900 border-zinc-800"><CardHeader><CardTitle className="text-white text-base">Ajuste Manual</CardTitle><CardDescription className="text-zinc-400">Creditar ou debitar AWA de um usuário</CardDescription></CardHeader><CardContent className="space-y-3">
        <div><Label className="text-zinc-300">E-mail do Usuário</Label><Input placeholder="email@exemplo.com" value={adjustForm.user_email} onChange={e => setAdjustForm({...adjustForm, user_email: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" data-testid="awa-adjust-email" /></div>
        <div><Label className="text-zinc-300">Quantidade AWA (+ creditar, - debitar)</Label><Input type="number" placeholder="1000 ou -500" value={adjustForm.amount} onChange={e => setAdjustForm({...adjustForm, amount: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" data-testid="awa-adjust-amount" /></div>
        <div><Label className="text-zinc-300">Motivo</Label><Input placeholder="Motivo do ajuste..." value={adjustForm.reason} onChange={e => setAdjustForm({...adjustForm, reason: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" data-testid="awa-adjust-reason" /></div>
        <Button onClick={adjustAwa} disabled={adjusting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="awa-adjust-btn">{adjusting ? 'Processando...' : 'Aplicar Ajuste'}</Button>
      </CardContent></Card>
      {data?.recent_transactions?.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800"><CardHeader><CardTitle className="text-white text-base">Transações Recentes</CardTitle></CardHeader><CardContent className="p-0"><div className="overflow-x-auto"><table className="w-full"><thead className="bg-zinc-800/60"><tr>
          <th className="px-4 py-2 text-left text-xs text-zinc-400">Tipo</th><th className="px-4 py-2 text-left text-xs text-zinc-400">Valor</th><th className="px-4 py-2 text-left text-xs text-zinc-400">Descrição</th><th className="px-4 py-2 text-left text-xs text-zinc-400">Data</th>
        </tr></thead><tbody className="divide-y divide-zinc-800">
          {data.recent_transactions.map((t, i) => (<tr key={i} className="hover:bg-zinc-800/40">
            <td className="px-4 py-2 text-xs text-zinc-300">{t.type}</td>
            <td className={`px-4 py-2 text-xs font-medium ${t.amount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{t.amount >= 0 ? '+' : ''}{t.amount} AWA</td>
            <td className="px-4 py-2 text-xs text-zinc-400">{t.description || '-'}</td>
            <td className="px-4 py-2 text-xs text-zinc-400">{t.created_at ? new Date(t.created_at).toLocaleString('pt-BR') : '-'}</td>
          </tr>))}
        </tbody></table></div></CardContent></Card>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/*              6. CONTEÚDO (Posts)            */
/* ═══════════════════════════════════════════ */
function PostsPage() {
  const { getHeaders } = useAdminAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('recent');

  const load = async () => { setLoading(true); try { const r = await axios.get(`${API}/api/admin/moderation/posts?filter_type=${filter}`, { headers: getHeaders() }); setPosts(r.data.posts); } catch { toast.error('Erro'); } finally { setLoading(false); } };
  useEffect(() => { load(); }, [filter]);

  const del = async (id) => { try { await axios.delete(`${API}/api/admin/moderation/posts/${id}?reason=Admin`, { headers: getHeaders() }); toast.success('Post excluído'); load(); } catch { toast.error('Erro'); } };

  return (
    <div className="space-y-4" data-testid="admin-posts">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Conteúdo (Posts)</h1>
        <Tabs value={filter} onValueChange={setFilter}><TabsList className="bg-zinc-800"><TabsTrigger value="recent">Recentes</TabsTrigger><TabsTrigger value="reported">Denunciados</TabsTrigger></TabsList></Tabs>
      </div>
      {loading ? <Spin /> : posts.length === 0 ? <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-8 text-center text-zinc-400">Nenhum post</CardContent></Card> : (
        <div className="space-y-3">
          {posts.map(p => (
            <Card key={p.post_id} className="bg-zinc-900 border-zinc-800"><CardContent className="p-4 flex gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1"><span className="font-medium text-white text-sm">{p.author_name || 'Usuário'}</span><span className="text-xs text-zinc-500">{p.created_at ? new Date(p.created_at).toLocaleString('pt-BR') : ''}</span>{p.report_reason && <Badge variant="destructive" className="ml-auto text-xs"><AlertTriangle className="h-3 w-3 mr-1" />{p.report_reason}</Badge>}</div>
                <p className="text-zinc-300 text-sm">{p.content}</p>
                {p.image_url && <img src={p.image_url.startsWith('http') ? p.image_url : `${API}${p.image_url}`} alt="" className="mt-2 max-h-32 rounded" />}
              </div>
              <Button size="sm" variant="outline" className="text-red-400 border-red-400/30 shrink-0" onClick={() => del(p.post_id)}><Trash2 className="h-4 w-4" /></Button>
            </CardContent></Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/*              7. ASSETS                     */
/* ═══════════════════════════════════════════ */
function AssetsPage() {
  const { getHeaders } = useAdminAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [ar, ur] = await Promise.all([
        axios.get(`${API}/api/collectibles/plant-assets`, { headers: getHeaders() }).catch(() => ({ data: { assets: [] } })),
        axios.get(`${API}/api/admin/assets/uploads-list`, { headers: getHeaders() }).catch(() => ({ data: { uploads: [] } }))
      ]);
      const plants = (ar.data.assets || []).map(a => ({ ...a, type: 'plant', img: a.images?.display_url ? resolveImageUrl(a.images.display_url) : null }));
      const uploads = (ur.data.uploads || []).map(u => ({ ...u, type: 'upload', img: u.url ? `${API}${u.url}` : null }));
      setAssets([...plants, ...uploads]);
      setStats({ plants: plants.length, uploads: uploads.length });
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === 'all' ? assets : assets.filter(a => filter === 'plants' ? a.type === 'plant' : a.type === 'upload');

  return (
    <div className="space-y-4" data-testid="admin-assets">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Assets & Imagens</h1>
        <Button size="sm" variant="outline" onClick={load} className="border-zinc-700 text-zinc-300"><RefreshCw className="h-4 w-4 mr-1" />Atualizar</Button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat title="Plantas" value={stats.plants || 0} icon={Image} color="emerald" />
        <Stat title="Uploads" value={stats.uploads || 0} icon={FileText} color="blue" />
        <Stat title="Total" value={(stats.plants || 0) + (stats.uploads || 0)} icon={Image} color="amber" />
      </div>
      <div className="flex gap-2">
        {['all', 'plants', 'uploads'].map(f => (<Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} onClick={() => setFilter(f)} className={filter === f ? 'bg-emerald-600' : 'border-zinc-700 text-zinc-300'}>{f === 'all' ? 'Todos' : f === 'plants' ? 'Plantas' : 'Uploads'}</Button>))}
      </div>
      {loading ? <Spin /> : (
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-2">
          {filtered.map((a, i) => (
            <Card key={i} className="bg-zinc-900 border-zinc-800 overflow-hidden">
              <div className="aspect-square bg-zinc-950 flex items-center justify-center">{a.img ? <img src={a.img} alt={a.species_name || a.filename || ''} className="w-full h-full object-cover" loading="lazy" /> : <Image className="h-6 w-6 text-zinc-700" />}</div>
              <div className="p-1.5"><p className="text-[10px] text-white truncate">{a.species_name || a.filename || 'Asset'}</p><Badge variant="outline" className="text-[9px] px-1 py-0 border-zinc-700 text-zinc-500">{a.type === 'plant' ? a.stage || 'semente' : 'upload'}</Badge></div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/*        8. CURSOS / CERTIFICADOS            */
/* ═══════════════════════════════════════════ */
function CoursesPage() {
  const { getHeaders } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualEmail, setManualEmail] = useState('');
  const [issuing, setIssuing] = useState(false);

  const load = async () => { try { const r = await axios.get(`${API}/api/admin/courses/certified`, { headers: getHeaders() }); setData(r.data); } catch { toast.error('Erro'); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const issueCert = async () => {
    if (!manualEmail) return;
    setIssuing(true);
    try {
      await axios.post(`${API}/api/admin/courses/certificate/manual`, { user_email: manualEmail, score: 1.0 }, { headers: getHeaders() });
      toast.success('Certificado emitido'); setManualEmail(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); } finally { setIssuing(false); }
  };

  if (loading) return <Spin />;
  return (
    <div className="space-y-6" data-testid="admin-courses">
      <h1 className="text-2xl font-bold text-white">Cursos & Certificados</h1>
      <div className="grid grid-cols-3 gap-4">
        <Stat title="Matriculados" value={data?.total_enrolled || 0} icon={Users} color="blue" />
        <Stat title="Aprovados" value={data?.total_passed || 0} icon={CheckCircle} color="emerald" />
        <Stat title="Certificados" value={data?.total_certified || 0} icon={GraduationCap} color="amber" />
      </div>
      <Card className="bg-zinc-900 border-zinc-800"><CardHeader><CardTitle className="text-white text-base">Emitir Certificado Manual</CardTitle></CardHeader><CardContent className="flex gap-3">
        <Input placeholder="E-mail do profissional" value={manualEmail} onChange={e => setManualEmail(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="cert-manual-email" />
        <Button onClick={issueCert} disabled={issuing} className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="cert-manual-btn">{issuing ? 'Emitindo...' : 'Emitir'}</Button>
      </CardContent></Card>
      {(data?.certificates?.length || 0) > 0 && (
        <Card className="bg-zinc-900 border-zinc-800"><CardHeader><CardTitle className="text-white text-base">Profissionais Certificados</CardTitle></CardHeader><CardContent className="p-0"><div className="overflow-x-auto"><table className="w-full"><thead className="bg-zinc-800/60"><tr>
          <th className="px-4 py-2 text-left text-xs text-zinc-400">Nome</th><th className="px-4 py-2 text-left text-xs text-zinc-400">Curso</th><th className="px-4 py-2 text-left text-xs text-zinc-400">Score</th><th className="px-4 py-2 text-left text-xs text-zinc-400">Data</th>
        </tr></thead><tbody className="divide-y divide-zinc-800">
          {data.certificates.map((c, i) => (<tr key={i} className="hover:bg-zinc-800/40">
            <td className="px-4 py-2 text-white text-sm">{c.user_name}</td>
            <td className="px-4 py-2 text-zinc-400 text-xs">{c.course_title}</td>
            <td className="px-4 py-2"><Badge className="bg-emerald-500/20 text-emerald-400">{Math.round((c.score || 0) * 100)}%</Badge></td>
            <td className="px-4 py-2 text-zinc-400 text-xs">{c.completion_date ? new Date(c.completion_date).toLocaleDateString('pt-BR') : '-'}</td>
          </tr>))}
        </tbody></table></div></CardContent></Card>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/*           9. CONFIGURAÇÕES                 */
/* ═══════════════════════════════════════════ */
function SettingsPage() {
  const { getHeaders, adminUser } = useAdminAuth();
  const [platform, setPlatform] = useState(null);
  const [maintenance, setMaintenance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwords, setPasswords] = useState({ current: '', new_pw: '', confirm: '' });
  const [maintMsg, setMaintMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [pr, sr] = await Promise.all([
          axios.get(`${API}/api/admin/settings/platform`, { headers: getHeaders() }),
          axios.get(`${API}/api/admin/settings`, { headers: getHeaders() }),
        ]);
        setPlatform(pr.data); setMaintenance(sr.data.maintenance_mode); setMaintMsg(sr.data.maintenance_mode?.message || '');
      } catch { toast.error('Erro'); } finally { setLoading(false); }
    })();
  }, []);

  const updatePlatform = async (key, val) => {
    try {
      await axios.put(`${API}/api/admin/settings/platform`, { [key]: val }, { headers: getHeaders() });
      toast.success('Atualizado'); setPlatform({ ...platform, [key]: val });
    } catch { toast.error('Erro'); }
  };

  const changePw = async () => {
    if (passwords.new_pw !== passwords.confirm) { toast.error('Senhas não coincidem'); return; }
    try { await axios.post(`${API}/api/admin/change-password`, { current_password: passwords.current, new_password: passwords.new_pw }, { headers: getHeaders() }); toast.success('Senha alterada'); setPasswords({ current: '', new_pw: '', confirm: '' }); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const toggleMaint = async (enabled) => {
    try { await axios.post(`${API}/api/admin/settings/maintenance`, { enabled, message: maintMsg }, { headers: getHeaders() }); toast.success(`Manutenção ${enabled ? 'ativada' : 'desativada'}`); setMaintenance({ enabled, message: maintMsg }); } catch { toast.error('Erro'); }
  };

  if (loading) return <Spin />;
  return (
    <div className="space-y-6" data-testid="admin-settings">
      <h1 className="text-2xl font-bold text-white">Configurações</h1>
      {/* Platform */}
      <Card className="bg-zinc-900 border-zinc-800"><CardHeader><CardTitle className="text-white text-base">Parâmetros da Plataforma</CardTitle></CardHeader><CardContent className="space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div><Label className="text-zinc-300">Supervisão (R$)</Label><Input type="number" value={platform?.supervision_price_brl || 35} onBlur={e => updatePlatform('supervision_price_brl', parseFloat(e.target.value))} onChange={e => setPlatform({...platform, supervision_price_brl: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" /></div>
          <div><Label className="text-zinc-300">Comissão Plataforma (%)</Label><Input type="number" value={platform?.platform_commission_pct || 20} onBlur={e => updatePlatform('platform_commission_pct', parseFloat(e.target.value))} onChange={e => setPlatform({...platform, platform_commission_pct: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" /></div>
          <div><Label className="text-zinc-300">Taxa AWA (fixo)</Label><Input type="number" value={platform?.awa_rate || 1000} disabled className="bg-zinc-800 border-zinc-700 text-zinc-500" /><p className="text-[10px] text-zinc-500 mt-1">1000 AWA = R$ 1,00 (fixo)</p></div>
        </div>
      </CardContent></Card>
      {/* Password */}
      <Card className="bg-zinc-900 border-zinc-800"><CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Key className="h-4 w-4" />Alterar Senha</CardTitle></CardHeader><CardContent className="space-y-3">
        <Input type="password" placeholder="Senha atual" value={passwords.current} onChange={e => setPasswords({...passwords, current: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" />
        <Input type="password" placeholder="Nova senha" value={passwords.new_pw} onChange={e => setPasswords({...passwords, new_pw: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" />
        <Input type="password" placeholder="Confirmar nova senha" value={passwords.confirm} onChange={e => setPasswords({...passwords, confirm: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" />
        <Button onClick={changePw} className="bg-emerald-600 hover:bg-emerald-700">Alterar Senha</Button>
      </CardContent></Card>
      {/* Maintenance */}
      <Card className="bg-zinc-900 border-zinc-800"><CardHeader><CardTitle className="text-white text-base">Modo Manutenção</CardTitle></CardHeader><CardContent className="space-y-3">
        <Input placeholder="Mensagem de manutenção..." value={maintMsg} onChange={e => setMaintMsg(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" />
        {maintenance?.enabled ? <Button variant="outline" className="border-emerald-500 text-emerald-400" onClick={() => toggleMaint(false)}>Desativar Manutenção</Button> : <Button variant="outline" className="border-amber-500 text-amber-400" onClick={() => toggleMaint(true)}>Ativar Manutenção</Button>}
        {maintenance?.enabled && <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded text-amber-200 text-sm">Manutenção ATIVADA</div>}
      </CardContent></Card>
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/*              ADMIN LAYOUT                  */
/* ═══════════════════════════════════════════ */
function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { adminUser, logout } = useAdminAuth();
  const [open, setOpen] = useState(true);

  const menu = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/users', icon: Users, label: 'Usuários' },
    { path: '/admin/whitelist', icon: Diamond, label: 'Whitelist' },
    { path: '/admin/finance', icon: DollarSign, label: 'Financeiro' },
    { path: '/admin/awa', icon: Wallet, label: 'AWA Wallet' },
    { path: '/admin/posts', icon: FileText, label: 'Conteúdo' },
    { path: '/admin/assets', icon: Image, label: 'Assets' },
    { path: '/admin/courses', icon: GraduationCap, label: 'Cursos' },
    { path: '/admin/referrals', icon: UserPlus, label: 'Indicações' },
    { path: '/admin/opportunities', icon: Briefcase, label: 'Vagas' },
    { path: '/admin/data-export', icon: FileSpreadsheet, label: 'Dados Científicos' },
    { path: '/admin/settings', icon: Settings, label: 'Configurações' },
  ];

  const isActive = (p, exact) => exact ? location.pathname === p : location.pathname.startsWith(p);

  return (
    <div className="min-h-screen bg-zinc-950 flex" data-testid="admin-layout">
      <aside className={`${open ? 'w-56' : 'w-14'} bg-zinc-900 border-r border-zinc-800 flex flex-col transition-all duration-200`}>
        <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
          {open && <div className="flex items-center gap-2"><Shield className="h-6 w-6 text-emerald-500" /><span className="font-bold text-white text-sm">Admin</span></div>}
          <Button variant="ghost" size="icon" onClick={() => setOpen(!open)} className="text-zinc-400 hover:text-white h-8 w-8"><Menu className="h-4 w-4" /></Button>
        </div>
        <nav className="flex-1 p-1.5 space-y-0.5">
          {menu.map(m => (
            <Link key={m.path} to={m.path}>
              <Button variant="ghost" className={`w-full justify-start gap-2.5 h-9 text-sm ${isActive(m.path, m.exact) ? 'bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`}>
                <m.icon className="h-4 w-4 shrink-0" />{open && <span>{m.label}</span>}
              </Button>
            </Link>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-800">
          {open && <div className="mb-2 text-xs text-zinc-500 truncate">{adminUser?.email}</div>}
          <Button variant="ghost" className="w-full justify-start gap-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 text-sm" onClick={logout}><LogOut className="h-4 w-4" />{open && <span>Sair</span>}</Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto"><div className="p-6 max-w-7xl">
        <Routes>
          <Route index element={<DashboardPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="whitelist" element={<WhitelistPage />} />
          <Route path="finance" element={<FinancePage />} />
          <Route path="awa" element={<AwaPage />} />
          <Route path="posts" element={<PostsPage />} />
          <Route path="assets" element={<AssetsPage />} />
          <Route path="courses" element={<CoursesPage />} />
          <Route path="referrals" element={<ReferralsAdminPage />} />
          <Route path="opportunities" element={<OpportunitiesAdminPage />} />
          <Route path="data-export" element={<DataExportAdminPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Routes>
      </div></main>
    </div>
  );
}

export { AdminLayout, AdminProtectedRoute };
export default AdminLayout;
